import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 */

/** How long "Added ✓" shows before the cart drawer opens */
const ADDED_DURATION = 1500;
/** How long an inline error stays in the bar */
const MESSAGE_DURATION = 4000;
/** Safety net if the cart never answers */
const ADD_TIMEOUT = 10000;

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 * - The button triggers the main buy box's Add to cart (same quantity / variant / cart logic).
 * - The size dropdowns select the matching option in the main variant picker; the theme's normal
 *   variant update then re-renders this bar, so both pickers stay in sync.
 * - Feedback: "Adding…" → "Added ✓" (1.5s) → cart drawer opens; errors show inline in the bar.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #pendingAdd = false;

  /** @type {number | undefined} */
  #stateTimeout;

  /** @type {number | undefined} */
  #messageTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    clearTimeout(this.#stateTimeout);
    clearTimeout(this.#messageTimeout);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar: triggers the main buy box's button.
   */
  handleAddToCartClick = () => {
    if (this.refs.addToCartButton.disabled || this.refs.stickyBar.dataset.state !== 'idle') return;

    const target = this.#targetAddToCartButton;
    if (!target) return;

    if (target.disabled) {
      this.#showMessage(this.#soldOutText());
      return;
    }

    this.#clearMessage();
    this.#setState('adding');
    this.#pendingAdd = true;
    this.#stateTimeout = setTimeout(() => {
      this.#pendingAdd = false;
      this.#setState('idle');
    }, ADD_TIMEOUT);

    target.dataset.puppet = 'true';
    target.click();
  };

  /**
   * A size dropdown in the bar changed: select the same value in the main variant picker.
   * The main picker's normal change handling fetches the new variant and re-renders this bar.
   * @param {Event} event
   */
  handleOptionChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const optionName = select.dataset.optionName;
    const value = select.value;
    if (!optionName) return;

    const picker = this.#getMainVariantPicker();
    if (!picker) return;

    const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
      (input) =>
        input instanceof HTMLInputElement && input.name.startsWith(`${optionName}-`) && input.value === value
    );

    if (radio instanceof HTMLInputElement) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // Main picker rendered as dropdowns instead of buttons
      const mainSelect = Array.from(picker.querySelectorAll('select')).find(
        (el) => el instanceof HTMLSelectElement && el.name.startsWith(`${optionName}-`)
      );
      if (!(mainSelect instanceof HTMLSelectElement)) return;
      const match = Array.from(mainSelect.options).find((option) => option.value === value || option.textContent?.trim() === value);
      if (!match) return;
      mainSelect.value = match.value;
      mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#clearMessage();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Dropdowns show the server-selected values (a select the user touched keeps its own value otherwise)
    this.#syncSelects();

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const variantPicker = this.#getMainVariantPicker();
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Cart updated: if it came from our click, show "Added ✓", then open the cart drawer.
   * @param {CustomEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (!this.#pendingAdd) return;
    const data = event.detail?.data || {};
    if (data.productId && String(data.productId) !== String(this.dataset.productId)) return;

    this.#pendingAdd = false;
    clearTimeout(this.#stateTimeout);

    if (data.didError) {
      this.#setState('idle');
      return;
    }

    this.#setState('added');
    this.#stateTimeout = setTimeout(() => {
      this.#setState('idle');
      openCartDrawer();
    }, ADDED_DURATION);
  };

  /**
   * Cart error from our click: short inline message in the bar.
   * @param {CustomEvent} event - The cart error event
   */
  #handleCartError = (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (!this.#pendingAdd) return;
    this.#pendingAdd = false;
    clearTimeout(this.#stateTimeout);
    this.#setState('idle');

    const data = event.detail?.data || {};
    const text = typeof data.description === 'string' && data.description ? data.description : data.message;
    this.#showMessage(text || this.#soldOutText());
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * @param {'idle' | 'adding' | 'added'} state
   */
  #setState(state) {
    this.refs.stickyBar.dataset.state = state;
    this.refs.addToCartButton.setAttribute('aria-busy', state === 'adding' ? 'true' : 'false');
    if (state === 'added') {
      this.refs.addToCartButton.setAttribute('aria-label', 'Added to cart');
    } else {
      this.refs.addToCartButton.removeAttribute('aria-label');
    }
  }

  /**
   * @param {string} text
   */
  #showMessage(text) {
    const message = this.querySelector('.sticky-add-to-cart__message');
    if (!message) return;
    message.textContent = text;
    clearTimeout(this.#messageTimeout);
    this.#messageTimeout = setTimeout(() => this.#clearMessage(), MESSAGE_DURATION);
  }

  #clearMessage() {
    const message = this.querySelector('.sticky-add-to-cart__message');
    if (message) message.textContent = '';
  }

  #soldOutText() {
    return 'Sorry, this size is sold out.';
  }

  /** Keep the dropdowns on the values the server marked as selected */
  #syncSelects() {
    this.querySelectorAll('.sticky-add-to-cart__select').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const selected = select.querySelector('option[selected]');
      if (selected instanceof HTMLOptionElement) select.value = selected.value;
    });
  }

  /**
   * The main (buy box) variant picker for this product
   * @returns {HTMLElement | null}
   */
  #getMainVariantPicker() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    return (
      section?.querySelector(`variant-picker[data-product-id="${productId}"]`) ??
      document.querySelector(`variant-picker[data-product-id="${productId}"]`)
    );
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

/**
 * Opens the cart drawer, if the theme uses one on this page.
 */
function openCartDrawer() {
  const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (
    document.querySelector('cart-drawer-component')
  );
  drawer?.open?.();
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
